<template>
  <div class="test-results-page">
    <div class="mb-6 flex flex-wrap items-center justify-between gap-3">
      <div>
        <h2 class="text-2xl font-bold text-neutral-900 dark:text-white">
          {{ t('pages.workspaces.testresults.title') }}
        </h2>
        <p class="mt-1 text-muted">
          {{ t('pages.workspaces.testresults.subtitle') }}
        </p>
      </div>
      <div class="flex flex-wrap gap-2">
        <UButton
          icon="i-lucide-trash"
          :label="t('pages.workspaces.testresults.actions.expungeExpired')"
          color="error"
          @click="openExpungeDialog"
          variant="outline"
        />
        <UButton
          icon="i-lucide-upload"
          :label="t('pages.workspaces.testresults.actions.import')"
          color="neutral"
          @click="openImportDialog"
          variant="outline"
        />
        <UButton
          icon="i-lucide-plus"
          :label="t('pages.workspaces.testresults.actions.create')"
          color="neutral"
          @click="openCreateDialog"
        />
      </div>
    </div>

    <div class="mb-6 flex flex-wrap gap-4">
      <AppIconField class="min-w-64 flex-1">
        <AppInputIcon name="i-lucide-search" />
        <UInput
          v-model="filters.username"
          :placeholder="t('pages.workspaces.testresults.filters.searchPlaceholder')"
          class="w-full"
          @update:model-value="debouncedLoadResults"
        />
      </AppIconField>

      <AppSelect
        v-model="filters.language"
        :options="languageOptions"
        option-label="label"
        option-value="value"
        :placeholder="t('pages.workspaces.testresults.filters.allLanguages')"
        class="w-48"
        show-clear
        @update:model-value="loadResults"
      />

      <AppSelect
        v-model="filters.status"
        :options="statusOptions"
        option-label="label"
        option-value="value"
        :placeholder="t('pages.workspaces.testresults.filters.allStatuses')"
        class="w-40"
        show-clear
        @update:model-value="loadResults"
      />

      <AppSelect
        v-model="filters.source"
        :options="sourceOptions"
        option-label="label"
        option-value="value"
        :placeholder="t('pages.workspaces.testresults.filters.allSources')"
        class="w-40"
        show-clear
        @update:model-value="loadResults"
      />

      <AppSelect
        v-model="eligibleLevelFilter"
        :options="gradeOptions"
        option-label="label"
        option-value="value"
        :placeholder="t('pages.workspaces.testresults.filters.eligibleLevels')"
        class="w-48"
        show-clear
      />
    </div>

    <AppDataTableShell
      :data="filteredResults"
      :columns="columns"
      :loading="loading"
      paginate
      :page-size="pageSize"
    >
      <template #empty>
        <div class="py-12 text-center">
          <UIcon name="i-lucide-file-spreadsheet" class="mb-4 text-5xl text-dimmed" />
          <p class="text-lg font-medium text-neutral-700 dark:text-neutral-200">
            {{ t('pages.workspaces.testresults.empty.title') }}
          </p>
          <p class="text-muted">
            {{ t('pages.workspaces.testresults.empty.subtitle') }}
          </p>
        </div>
      </template>

      <template #userName-cell="{ row }">
        <span class="font-medium">{{ row.original.userName }}</span>
      </template>

      <template #matriculationNumber-cell="{ row }">
        {{ row.original.matriculationNumber ?? '-' }}
      </template>

      <template #language-cell="{ row }">
        {{ t(`enums.spokenLanguage.${row.original.language}`) }}
      </template>

      <template #percent-cell="{ row }">
        <span
          v-if="row.original.percent !== null && row.original.percent !== undefined"
          :class="getPercentClass(row.original.percent)"
        >
          {{ row.original.percent }}%
        </span>
        <span v-else class="text-dimmed">-</span>
      </template>

      <template #eligibleLevels-cell="{ row }">
        <span v-if="getEligibleLevels(row.original).length">
          {{ getEligibleLevelsLabel(row.original) }}
        </span>
        <span v-else class="text-dimmed">-</span>
      </template>

      <template #status-cell="{ row }">
        <UBadge
          :label="t(`enums.testResultStatus.${row.original.status}`)"
          :color="toAppColor(row.original.status === 'valid' ? 'success' : 'warn')"
        />
      </template>

      <template #source-cell="{ row }">
        <UBadge :label="t(`enums.testResultSource.${row.original.source}`)" color="neutral" />
      </template>

      <template #syncJobName-cell="{ row }">{{ row.original.syncJobName ?? '-' }}</template>

      <template #validUntilSemesterName-cell="{ row }">
        <span v-if="row.original.validUntilSemesterName">
          {{ row.original.validUntilSemesterName }}
        </span>
        <span v-else class="text-dimmed">
          {{ t('pages.workspaces.testresults.validUntilAlways') }}
        </span>
      </template>

      <template #createdAt-cell="{ row }">
        {{ d(new Date(row.original.createdAt), 'datetime') }}
      </template>

      <template #actions-cell="{ row }">
        <div class="flex gap-1">
          <UButton
            icon="i-lucide-pencil"
            size="sm"
            :aria-label="t('pages.workspaces.testresults.actions.edit')"
            @click="openEditDialog(row.original)"
            variant="ghost"
          />
          <UButton
            v-if="row.original.status === 'valid'"
            icon="i-lucide-ban"
            size="sm"
            color="warning"
            :aria-label="t('pages.workspaces.testresults.actions.invalidate')"
            @click="confirmInvalidate(row.original)"
            variant="ghost"
          />
          <UButton
            v-else
            icon="i-lucide-circle-check"
            size="sm"
            color="success"
            :aria-label="t('pages.workspaces.testresults.actions.revalidate')"
            @click="handleRevalidate(row.original)"
            variant="ghost"
          />
          <UButton
            v-if="row.original.source !== 'ilias'"
            icon="i-lucide-trash"
            size="sm"
            color="error"
            :aria-label="t('pages.workspaces.testresults.actions.delete')"
            @click="confirmDelete(row.original)"
            variant="ghost"
          />
        </div>
      </template>
    </AppDataTableShell>

    <AppModal
      v-model:open="showDialog"
      :title="
        selectedResult
          ? t('pages.workspaces.testresults.dialog.editTitle')
          : t('pages.workspaces.testresults.dialog.createTitle')
      "
      modal
      :style="{ width: '32rem' }"
    >
      <div class="flex flex-col gap-4">
        <div v-if="!selectedResult">
          <label class="mb-2 block text-sm font-medium">
            {{ t('pages.workspaces.testresults.fields.user') }}
          </label>
          <UserSearchSelect
            v-model="selectedUser"
            include-students
            :class="{ 'border-error ring-1 ring-error': formErrors.user }"
          />
          <small class="text-muted">{{ t('pages.workspaces.testresults.fields.userHelp') }}</small>
          <small v-if="formErrors.user" class="block text-red-700 dark:text-red-400">{{
            formErrors.user
          }}</small>
          <div v-if="selectedUser?.matriculationId" class="mt-2 text-sm text-neutral-600">
            {{ t('pages.workspaces.testresults.fields.matriculationNumberDisplay') }}:
            {{ selectedUser.matriculationId }}
          </div>
        </div>

        <div v-if="!selectedResult">
          <label class="mb-2 block text-sm font-medium">
            {{ t('pages.workspaces.testresults.fields.language') }}
          </label>
          <AppSelect
            v-model="formData.language"
            :options="languageOptionsForForm"
            option-label="label"
            option-value="value"
            class="w-full"
            :class="{ 'border-error ring-1 ring-error': formErrors.language }"
          />
          <small v-if="formErrors.language" class="block text-red-700 dark:text-red-400">{{
            formErrors.language
          }}</small>
        </div>

        <div v-if="!isGradeBasedEntry">
          <label class="mb-2 block text-sm font-medium">
            {{ t('pages.workspaces.testresults.fields.percent') }}
          </label>
          <UInputNumber
            v-model="formData.percent"
            class="w-full"
            :min="0"
            :max="100"
            :format-options="{ style: 'unit', unit: 'percent', unitDisplay: 'short' }"
            :class="{ 'border-error ring-1 ring-error': formErrors.percent }"
            :increment="false"
            :decrement="false"
          />
          <small class="text-muted">{{
            t('pages.workspaces.testresults.fields.percentHelp')
          }}</small>
          <small v-if="formErrors.percent" class="block text-red-700 dark:text-red-400">{{
            formErrors.percent
          }}</small>
        </div>

        <div v-if="isGradeBasedEntry">
          <label class="mb-2 block text-sm font-medium">
            {{ t('pages.workspaces.testresults.fields.grade') }}
          </label>
          <AppSelect
            v-model="formData.grade"
            :options="gradeOptions"
            option-label="label"
            option-value="value"
            class="w-full"
            show-clear
          />
          <small class="text-muted">{{ t('pages.workspaces.testresults.fields.gradeHelp') }}</small>
          <small v-if="formErrors.grade" class="block text-red-700 dark:text-red-400">{{
            formErrors.grade
          }}</small>
        </div>

        <div>
          <label class="mb-2 block text-sm font-medium">
            {{ t('pages.workspaces.testresults.fields.validUntilSemester') }}
          </label>
          <AppSelect
            v-model="formData.validUntilSemesterId"
            :options="semesterOptions"
            option-label="label"
            option-value="value"
            class="w-full"
            show-clear
            :placeholder="t('pages.workspaces.testresults.fields.validUntilAlways')"
          />
          <small class="text-muted">{{
            t('pages.workspaces.testresults.fields.validUntilHelp')
          }}</small>
        </div>
      </div>

      <template #footer>
        <UButton :label="t('common.actions.cancel')" @click="showDialog = false" variant="ghost" />
        <UButton :label="t('common.actions.save')" :loading="saving" @click="handleSave" />
      </template>
    </AppModal>

    <AppModal
      v-model:open="showImportDialog"
      :title="t('pages.workspaces.testresults.import.title')"
      modal
      :style="{ maxWidth: '72rem' }"
    >
      <div class="flex flex-col gap-5">
        <div class="grid gap-4 md:grid-cols-2">
          <div class="flex flex-col gap-2 md:col-span-2">
            <label class="text-sm font-medium">
              {{ t('pages.workspaces.testresults.import.fields.file') }}
            </label>
            <AppFileUpload
              mode="basic"
              accept=".xlsx"
              :max-file-size="10485760"
              :auto="false"
              :choose-label="t('pages.workspaces.testresults.import.actions.chooseFile')"
              @select="onImportFileSelect"
              @clear="onImportFileClear"
            />
            <small class="text-muted">
              {{ t('pages.workspaces.testresults.import.fields.fileHelp') }}
            </small>
            <small v-if="selectedImportFile" class="text-neutral-600">
              {{ selectedImportFile.name }}
            </small>
          </div>

          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium">
              {{ t('pages.workspaces.testresults.import.fields.language') }}
            </label>
            <AppSelect
              v-model="importLanguage"
              :options="languageOptionsForForm"
              option-label="label"
              option-value="value"
              class="w-full"
            />
          </div>

          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium">
              {{ t('pages.workspaces.testresults.import.fields.validUntilSemester') }}
            </label>
            <AppSelect
              v-model="importValidUntilSemesterId"
              :options="semesterOptions"
              option-label="label"
              option-value="value"
              class="w-full"
              show-clear
              :placeholder="t('pages.workspaces.testresults.fields.validUntilAlways')"
            />
            <small class="text-muted">
              {{ t('pages.workspaces.testresults.import.fields.validUntilHelp') }}
            </small>
          </div>
        </div>

        <AppAlert v-if="!languageOptionsForForm.length" color="warning" :closable="false">
          {{ t('pages.workspaces.testresults.import.messages.noLanguages') }}
        </AppAlert>

        <AppAlert v-if="importError" color="error" :closable="false">
          {{ importError }}
        </AppAlert>

        <div class="flex justify-end">
          <UButton
            icon="i-lucide-upload"
            :label="t('pages.workspaces.testresults.import.actions.submit')"
            :loading="importing"
            :disabled="
              !selectedImportFile || !importLanguage || importing || !languageOptionsForForm.length
            "
            @click="handleImport"
          />
        </div>

        <div v-if="importResults" class="space-y-4">
          <div class="grid gap-3 md:grid-cols-4">
            <div class="rounded border border-neutral-200 p-3 dark:border-neutral-700">
              <div class="text-sm text-muted">
                {{ t('pages.workspaces.testresults.import.summary.total') }}
              </div>
              <div class="text-xl font-semibold">{{ importResults.totalRows }}</div>
            </div>
            <div class="rounded border border-neutral-200 p-3 dark:border-neutral-700">
              <div class="text-sm text-muted">
                {{ t('pages.workspaces.testresults.import.summary.created') }}
              </div>
              <div class="text-xl font-semibold text-green-700 dark:text-green-400">
                {{ importResults.createdCount }}
              </div>
            </div>
            <div class="rounded border border-neutral-200 p-3 dark:border-neutral-700">
              <div class="text-sm text-muted">
                {{ t('pages.workspaces.testresults.import.summary.updated') }}
              </div>
              <div class="text-xl font-semibold text-blue-700 dark:text-blue-400">
                {{ importResults.updatedCount }}
              </div>
            </div>
            <div class="rounded border border-neutral-200 p-3 dark:border-neutral-700">
              <div class="text-sm text-muted">
                {{ t('pages.workspaces.testresults.import.summary.failed') }}
              </div>
              <div class="text-xl font-semibold text-red-700 dark:text-red-400">
                {{ importResults.failedCount }}
              </div>
            </div>
          </div>

          <AppDataTableShell :data="importResults.rows" :columns="importColumns">
            <template #action-cell="{ row }">
              <UBadge
                :label="getImportActionLabel(row.original.action)"
                :color="toAppColor(getImportActionSeverity(row.original.action))"
              />
            </template>
            <template #resolvedUserName-cell="{ row }">
              {{ row.original.resolvedUserName ?? '-' }}
            </template>
            <template #resolvedMatriculationNumber-cell="{ row }">
              {{ row.original.resolvedMatriculationNumber ?? '-' }}
            </template>
            <template #error-cell="{ row }">
              <span v-if="row.original.error" class="text-sm text-red-700 dark:text-red-400">
                {{ row.original.error }}
              </span>
              <span v-else>-</span>
            </template>
          </AppDataTableShell>
        </div>
      </div>

      <template #footer>
        <UButton
          :label="t('common.actions.close')"
          @click="showImportDialog = false"
          variant="ghost"
        />
      </template>
    </AppModal>

    <AppModal
      v-model:open="showExpungeDialog"
      :title="t('pages.workspaces.testresults.expunge.title')"
      modal
      :style="{ width: '32rem' }"
    >
      <div class="flex flex-col gap-4">
        <AppAlert color="warning" :closable="false">
          {{ t('pages.workspaces.testresults.expunge.warning') }}
        </AppAlert>

        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium">
            {{ t('pages.workspaces.testresults.expunge.fields.semester') }}
          </label>
          <AppSelect
            v-model="expungeSemesterId"
            :options="semesterOptions"
            option-label="label"
            option-value="value"
            class="w-full"
          />
          <small class="text-muted">
            {{ t('pages.workspaces.testresults.expunge.fields.semesterHelp') }}
          </small>
          <small class="text-muted">
            {{ t('pages.workspaces.testresults.expunge.fields.includesSelectedSemester') }}
          </small>
        </div>

        <AppAlert v-if="expungeError" color="error" :closable="false">
          {{ expungeError }}
        </AppAlert>
      </div>

      <template #footer>
        <UButton
          :label="t('common.actions.cancel')"
          @click="showExpungeDialog = false"
          variant="ghost"
        />
        <UButton
          icon="i-lucide-trash"
          :label="t('pages.workspaces.testresults.expunge.actions.submit')"
          color="error"
          :loading="expunging"
          :disabled="!expungeSemesterId || expunging"
          @click="handleExpungeExpired"
        />
      </template>
    </AppModal>
  </div>
</template>

<script setup lang="ts">
import type { TableColumn } from '@nuxt/ui'

import { isAxiosError } from 'axios'
import { useI18n } from 'vue-i18n'

import type { Language, LanguageLevelType } from '@/types/languages'
import type { SemesterListItem } from '@/types/semester'
import type {
  LanguageTestResultDTO,
  LanguageTestResultImportAction,
  LanguageTestResultImportResponse,
  LanguageTestResultSource,
  LanguageTestResultStatus,
  UpdateLanguageTestResultRequest,
  WorkspaceSyncJobDTO,
} from '@/types/testResult'
import type { UserSearchResultDTO } from '@/types/user'
import type { WorkspaceDTO } from '@/types/workspace'

import { toAppColor } from '@/composables/useAppColor'
import { appConfirm } from '@/composables/useAppConfirm'
import { useAppToast } from '@/composables/useAppToast'
import { useMetadata } from '@/composables/useMetadata'
import {
  createManualTestResult,
  deleteWorkspaceTestResult,
  expungeExpiredWorkspaceTestResults,
  getWorkspaceTestResultSemesters,
  getWorkspaceTestResults,
  getWorkspaceSyncJobs,
  importWorkspaceTestResults,
  invalidateTestResult,
  revalidateTestResult,
  updateWorkspaceTestResult,
} from '@/services/testResultService'
import { OrderedLanguages, OrderedLanguageLevels } from '@/types/languages'
import { getApiErrorMessage } from '@/utils/apiError'

const props = defineProps<{
  workspace: WorkspaceDTO
}>()

definePage({
  meta: {
    requiresAuth: true,
    requiresInternalAccess: true,
  },
})

const { t, d } = useI18n()
const toast = useAppToast()
const { availableSemesters, selectedSemester } = useMetadata()
type ImportResultRow = LanguageTestResultImportResponse['rows'][number]
const columns = computed<TableColumn<LanguageTestResultDTO>[]>(() => [
  { accessorKey: 'userName', header: t('pages.workspaces.testresults.columns.userName') },
  {
    accessorKey: 'matriculationNumber',
    header: t('pages.workspaces.testresults.columns.matriculationNumber'),
  },
  { accessorKey: 'language', header: t('pages.workspaces.testresults.columns.language') },
  { accessorKey: 'percent', header: t('pages.workspaces.testresults.columns.percent') },
  { id: 'eligibleLevels', header: t('pages.workspaces.testresults.columns.eligibleLevels') },
  { accessorKey: 'status', header: t('pages.workspaces.testresults.columns.status') },
  { accessorKey: 'source', header: t('pages.workspaces.testresults.columns.source') },
  { accessorKey: 'syncJobName', header: t('pages.workspaces.testresults.columns.syncJob') },
  {
    accessorKey: 'validUntilSemesterName',
    header: t('pages.workspaces.testresults.columns.validUntil'),
  },
  { accessorKey: 'createdAt', header: t('pages.workspaces.testresults.columns.createdAt') },
  { id: 'actions', header: t('pages.workspaces.testresults.columns.actions') },
])
const importColumns = computed<TableColumn<ImportResultRow>[]>(() => [
  { accessorKey: 'rowNumber', header: t('pages.workspaces.testresults.import.columns.row') },
  {
    accessorKey: 'identifier',
    header: t('pages.workspaces.testresults.import.columns.identifier'),
  },
  { accessorKey: 'action', header: t('pages.workspaces.testresults.import.columns.status') },
  {
    accessorKey: 'resolvedUserName',
    header: t('pages.workspaces.testresults.import.columns.userName'),
  },
  {
    accessorKey: 'resolvedMatriculationNumber',
    header: t('pages.workspaces.testresults.import.columns.matriculationNumber'),
  },
  { accessorKey: 'error', header: t('pages.workspaces.testresults.import.columns.error') },
])

const results = ref<LanguageTestResultDTO[]>([])
const totalRecords = ref(0)
const loading = ref(false)
const pageSize = ref(20)
const syncJobs = ref<WorkspaceSyncJobDTO[]>([])
const validitySemesters = ref<SemesterListItem[]>([])

const filters = reactive({
  username: '',
  language: null as Language | null,
  status: null as LanguageTestResultStatus | null,
  source: null as LanguageTestResultSource | null,
})
const eligibleLevelFilter = ref<LanguageLevelType | null>(null)

const showDialog = ref(false)
const selectedResult = ref<LanguageTestResultDTO | null>(null)
const saving = ref(false)

const showImportDialog = ref(false)
const importing = ref(false)
const selectedImportFile = ref<File | null>(null)
const importLanguage = ref<Language | null>(null)
const importValidUntilSemesterId = ref<number | null>(null)
const importError = ref<string | null>(null)
const importResults = ref<LanguageTestResultImportResponse | null>(null)

const showExpungeDialog = ref(false)
const expunging = ref(false)
const expungeSemesterId = ref<number | null>(null)
const expungeError = ref<string | null>(null)

const selectedUser = ref<UserSearchResultDTO | null>(null)
const formData = reactive({
  language: null as Language | null,
  percent: null as number | null,
  grade: null as LanguageLevelType | null,
  validUntilSemesterId: null as number | null,
})

const formErrors = reactive({
  user: '',
  language: '',
  percent: '',
  grade: '',
})

const allowedLanguages = computed(() => {
  const languageSet = new Set<Language>()
  for (const job of syncJobs.value) {
    languageSet.add(job.language)
  }
  return OrderedLanguages.filter((lang) => languageSet.has(lang))
})

const languageOptions = computed(() =>
  allowedLanguages.value.map((lang) => ({
    label: t(`enums.spokenLanguage.${lang}`),
    value: lang,
  })),
)

const languageOptionsForForm = computed(() =>
  allowedLanguages.value.map((lang) => ({
    label: t(`enums.spokenLanguage.${lang}`),
    value: lang,
  })),
)

const gradeOptions = computed(() =>
  OrderedLanguageLevels.map((level) => ({
    label: t(`enums.languageLevel.${level}`),
    value: level,
  })),
)

const statusOptions = computed(() => [
  { label: t('enums.testResultStatus.valid'), value: 'valid' },
  { label: t('enums.testResultStatus.invalidated'), value: 'invalidated' },
])

const sourceOptions = computed(() => [
  { label: t('enums.testResultSource.ilias'), value: 'ilias' },
  { label: t('enums.testResultSource.manual'), value: 'manual' },
  { label: t('enums.testResultSource.import'), value: 'import' },
])

const validitySemesterSource = computed(() =>
  validitySemesters.value.length > 0 ? validitySemesters.value : availableSemesters.value,
)

const semesterOptions = computed(() =>
  validitySemesterSource.value.map((sem) => ({
    label: sem.nameDE,
    value: sem.id,
  })),
)

const isGradeBasedEntry = computed(
  () => !selectedResult.value || selectedResult.value.grade !== null,
)
let debounceTimeout: ReturnType<typeof setTimeout>

function debouncedLoadResults() {
  clearTimeout(debounceTimeout)
  debounceTimeout = setTimeout(() => {
    void loadResults()
  }, 300)
}

async function loadResults() {
  loading.value = true
  try {
    const response = await getWorkspaceTestResults(
      props.workspace.id,
      {
        language: filters.language ?? undefined,
        status: filters.status ?? undefined,
        source: filters.source ?? undefined,
        username: filters.username || undefined,
      },
      1,
      0,
    )
    results.value = response.items
    totalRecords.value = response.totalCount
  } catch (error) {
    console.error('Error loading test results:', error)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('pages.workspaces.testresults.messages.loadError'),
      duration: 3000,
    })
  } finally {
    loading.value = false
  }
}

async function loadSyncJobs() {
  try {
    syncJobs.value = await getWorkspaceSyncJobs(props.workspace.id)
  } catch (error) {
    console.error('Error loading sync jobs:', error)
  }
}

async function loadValiditySemesters() {
  try {
    const response = await getWorkspaceTestResultSemesters(props.workspace.id)
    validitySemesters.value = response.semesters
  } catch (error) {
    console.error('Error loading test result semesters:', error)
    validitySemesters.value = availableSemesters.value
  }
}

function getPercentClass(percent: number): string {
  if (percent >= 80) return 'text-green-600 dark:text-green-400 font-medium'
  if (percent >= 60) return 'text-yellow-600 dark:text-yellow-400'
  return 'text-red-600 dark:text-red-400'
}

const eligibleLevelRanges = [
  { level: 'A1', min: 0, max: 19 },
  { level: 'A2', min: 20, max: 52 },
  { level: 'B1', min: 47, max: 67 },
  { level: 'B2', min: 62, max: 82 },
  { level: 'C1', min: 77, max: 92 },
  { level: 'C2', min: 87, max: 100 },
] as const

function getEligibleLevels(result: LanguageTestResultDTO): LanguageLevelType[] {
  if (result.source !== 'ilias' && result.grade) {
    return [result.grade]
  }

  if (result.percent === null || result.percent === undefined) {
    return []
  }

  return eligibleLevelRanges
    .filter((range) => result.percent! >= range.min && result.percent! <= range.max)
    .map((range) => range.level)
}

function getEligibleLevelsLabel(result: LanguageTestResultDTO): string {
  return getEligibleLevels(result).join(', ')
}

const filteredResults = computed(() => {
  if (!eligibleLevelFilter.value) {
    return results.value
  }

  return results.value.filter((result) =>
    getEligibleLevels(result).includes(eligibleLevelFilter.value!),
  )
})

function advanceSemesterId(semesterId: number, count: number): number {
  const year = Math.floor(semesterId / 10)
  const semesterType = semesterId % 10
  const linearIndex = year * 2 + (semesterType - 1) + count
  const nextYear = Math.floor(linearIndex / 2)
  const nextType = (linearIndex % 2) + 1
  return nextYear * 10 + nextType
}

function getDefaultImportValidUntilSemesterId(): number | null {
  const currentSemesterId = selectedSemester.value?.id
  if (!currentSemesterId) {
    return null
  }

  const targetSemesterId = advanceSemesterId(currentSemesterId, 3)
  const exactMatch = validitySemesterSource.value.find(
    (semester) => semester.id === targetSemesterId,
  )
  if (exactMatch) {
    return exactMatch.id
  }

  const laterSemester = [...validitySemesterSource.value]
    .filter((semester) => semester.id > currentSemesterId)
    .sort((left, right) => left.id - right.id)[0]

  return laterSemester?.id ?? null
}

function getPreviousSemesterId(currentSemesterId: number): number | null {
  const previousSemester = [...validitySemesterSource.value]
    .filter((semester) => semester.id < currentSemesterId)
    .sort((left, right) => right.id - left.id)[0]

  return previousSemester?.id ?? null
}

function resetForm() {
  selectedUser.value = null
  formData.language = allowedLanguages.value[0] ?? null
  formData.percent = null
  formData.grade = null
  formData.validUntilSemesterId = selectedSemester.value?.id ?? null
  formErrors.user = ''
  formErrors.language = ''
  formErrors.percent = ''
  formErrors.grade = ''
}

function resetImportState() {
  selectedImportFile.value = null
  importLanguage.value = allowedLanguages.value[0] ?? null
  importValidUntilSemesterId.value = getDefaultImportValidUntilSemesterId()
  importError.value = null
  importResults.value = null
}

function resetExpungeState() {
  const currentSemesterId = selectedSemester.value?.id ?? null
  expungeSemesterId.value = currentSemesterId
    ? (getPreviousSemesterId(currentSemesterId) ?? currentSemesterId)
    : null
  expungeError.value = null
}

function openCreateDialog() {
  selectedResult.value = null
  resetForm()
  showDialog.value = true
}

function openEditDialog(result: LanguageTestResultDTO) {
  selectedResult.value = result
  formData.percent = result.percent ?? null
  formData.grade = result.grade ?? null
  formData.validUntilSemesterId = result.validUntilSemesterId ?? null
  formErrors.user = ''
  formErrors.language = ''
  formErrors.percent = ''
  formErrors.grade = ''
  showDialog.value = true
}

function openImportDialog() {
  resetImportState()
  showImportDialog.value = true
}

function openExpungeDialog() {
  resetExpungeState()
  showExpungeDialog.value = true
}

function validateForm(): boolean {
  let isValid = true
  formErrors.user = ''
  formErrors.language = ''
  formErrors.percent = ''
  formErrors.grade = ''

  if (!selectedResult.value) {
    if (!selectedUser.value) {
      formErrors.user = t('pages.workspaces.testresults.validation.userRequired')
      isValid = false
    }
    if (!formData.language) {
      formErrors.language = t('pages.workspaces.testresults.validation.languageRequired')
      isValid = false
    }
  }

  if (isGradeBasedEntry.value) {
    if (!formData.grade) {
      formErrors.grade = t('pages.workspaces.testresults.validation.gradeRequired')
      isValid = false
    }
  } else if (formData.percent === null || formData.percent === undefined) {
    formErrors.percent = t('pages.workspaces.testresults.validation.percentRequired')
    isValid = false
  } else if (formData.percent < 0 || formData.percent > 100) {
    formErrors.percent = t('pages.workspaces.testresults.validation.percentRange')
    isValid = false
  }

  return isValid
}

async function handleSave() {
  if (!validateForm()) return

  saving.value = true
  try {
    if (selectedResult.value) {
      const updateRequest: UpdateLanguageTestResultRequest = {}

      if (isGradeBasedEntry.value) {
        updateRequest.grade = formData.grade ?? undefined
      } else {
        updateRequest.percent = formData.percent ?? undefined
      }

      updateRequest.validUntilSemesterId = formData.validUntilSemesterId

      await updateWorkspaceTestResult(props.workspace.id, selectedResult.value.id, updateRequest)
      toast.add({
        color: 'success',
        title: t('common.messages.success'),
        description: t('pages.workspaces.testresults.messages.updateSuccess'),
        duration: 3000,
      })
    } else {
      const user = selectedUser.value!
      const resolvedUserName = user.account ?? user.userName ?? user.id
      await createManualTestResult(props.workspace.id, {
        userName: resolvedUserName,
        matriculationNumber: user.matriculationId || null,
        language: formData.language!,
        grade: formData.grade,
        validUntilSemesterId: formData.validUntilSemesterId,
      })
      toast.add({
        color: 'success',
        title: t('common.messages.success'),
        description: t('pages.workspaces.testresults.messages.createSuccess'),
        duration: 3000,
      })
    }

    showDialog.value = false
    await loadResults()
  } catch (error: unknown) {
    console.error('Error saving test result:', error)
    const message =
      isAxiosError(error) && error.response?.status === 409
        ? t('pages.workspaces.testresults.messages.duplicateError')
        : selectedResult.value
          ? t('pages.workspaces.testresults.messages.updateError')
          : t('pages.workspaces.testresults.messages.createError')
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: message,
      duration: 5000,
    })
  } finally {
    saving.value = false
  }
}

function onImportFileSelect(event: { files: File[] }) {
  selectedImportFile.value = event.files?.[0] ?? null
  importError.value = null
  importResults.value = null
}

function onImportFileClear() {
  selectedImportFile.value = null
  importError.value = null
  importResults.value = null
}

async function handleImport() {
  if (!selectedImportFile.value || !importLanguage.value) {
    return
  }

  importing.value = true
  importError.value = null
  importResults.value = null

  try {
    const response = await importWorkspaceTestResults(
      props.workspace.id,
      selectedImportFile.value,
      importLanguage.value,
      importValidUntilSemesterId.value,
    )
    importResults.value = response

    toast.add({
      color: response.failedCount > 0 ? 'warning' : 'success',
      title: t('pages.workspaces.testresults.import.messages.summaryTitle'),
      description: t('pages.workspaces.testresults.import.messages.summaryDetail', {
        created: response.createdCount,
        updated: response.updatedCount,
        failed: response.failedCount,
      }),
      duration: 5000,
    })

    await loadResults()
  } catch (error: unknown) {
    importError.value = getApiErrorMessage(
      error,
      t,
      t('pages.workspaces.testresults.import.messages.importError'),
    )
  } finally {
    importing.value = false
  }
}

async function handleExpungeExpired() {
  if (!expungeSemesterId.value) {
    return
  }

  expunging.value = true
  expungeError.value = null

  try {
    const response = await expungeExpiredWorkspaceTestResults(
      props.workspace.id,
      expungeSemesterId.value,
    )

    toast.add({
      color: response.deletedCount > 0 ? 'success' : 'info',
      title: t('pages.workspaces.testresults.expunge.messages.successTitle'),
      description: t('pages.workspaces.testresults.expunge.messages.successDetail', {
        count: response.deletedCount,
      }),
      duration: 5000,
    })

    showExpungeDialog.value = false
    await loadResults()
  } catch (error: unknown) {
    // The bare-string body the second branch used to handle is one of the shapes
    // getApiErrorMessage already understands.
    expungeError.value = getApiErrorMessage(
      error,
      t,
      t('pages.workspaces.testresults.expunge.messages.error'),
    )
  } finally {
    expunging.value = false
  }
}

function getImportActionSeverity(action: LanguageTestResultImportAction) {
  switch (action) {
    case 'created':
      return 'success'
    case 'updated':
      return 'info'
    default:
      return 'danger'
  }
}

function getImportActionLabel(action: LanguageTestResultImportAction) {
  return t(`pages.workspaces.testresults.import.actions.${action}`)
}

async function confirmInvalidate(result: LanguageTestResultDTO) {
  const accepted = await appConfirm({
    message: t('pages.workspaces.testresults.dialog.confirmInvalidate'),
    title: t('pages.workspaces.testresults.dialog.confirmInvalidateTitle'),
    cancelLabel: t('common.actions.cancel'),
    confirmLabel: t('pages.workspaces.testresults.actions.invalidate'),
    confirmColor: 'warning',
  })
  if (accepted) await handleInvalidate(result)
}

async function handleInvalidate(result: LanguageTestResultDTO) {
  try {
    await invalidateTestResult(props.workspace.id, result.id)
    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('pages.workspaces.testresults.messages.invalidateSuccess'),
      duration: 3000,
    })
    await loadResults()
  } catch (error) {
    console.error('Error invalidating test result:', error)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('pages.workspaces.testresults.messages.invalidateError'),
      duration: 3000,
    })
  }
}

async function handleRevalidate(result: LanguageTestResultDTO) {
  try {
    await revalidateTestResult(props.workspace.id, result.id)
    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('pages.workspaces.testresults.messages.revalidateSuccess'),
      duration: 3000,
    })
    await loadResults()
  } catch (error) {
    console.error('Error revalidating test result:', error)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('pages.workspaces.testresults.messages.revalidateError'),
      duration: 3000,
    })
  }
}

async function confirmDelete(result: LanguageTestResultDTO) {
  const accepted = await appConfirm({
    message: t('pages.workspaces.testresults.dialog.confirmDelete'),
    title: t('pages.workspaces.testresults.dialog.confirmDeleteTitle'),
    cancelLabel: t('common.actions.cancel'),
    confirmLabel: t('pages.workspaces.testresults.actions.delete'),
    confirmColor: 'error',
  })
  if (accepted) await handleDelete(result)
}

async function handleDelete(result: LanguageTestResultDTO) {
  try {
    await deleteWorkspaceTestResult(props.workspace.id, result.id)
    toast.add({
      color: 'success',
      title: t('common.messages.success'),
      description: t('pages.workspaces.testresults.messages.deleteSuccess'),
      duration: 3000,
    })
    await loadResults()
  } catch (error) {
    console.error('Error deleting test result:', error)
    toast.add({
      color: 'error',
      title: t('common.messages.error'),
      description: t('pages.workspaces.testresults.messages.deleteError'),
      duration: 3000,
    })
  }
}

onMounted(() => {
  void loadResults()
  void loadSyncJobs()
  void loadValiditySemesters()
})
</script>
